/**
 * Die Akzentfarbe eines Mandanten — und was aus ihr folgt (Signal, Paket 6).
 *
 * Seit dem 07.10.2026 hängt alles, was „drück hier" heißt, an einem Wert
 * (`--akzent`, packages/ui/src/tokens.css): Hauptknopf, mittiger Knopf am
 * Telefon, Marke. Luca: Die Farbe soll im Einstellungsmenü gesetzt werden
 * können. Gewählt wird **eine** Farbe; die Rollen daneben rechnet diese Datei,
 * und zwar an genau einer Stelle — dieselbe Rechnung läuft in der Maske
 * (Vorschau), beim Speichern im Server und beim Setzen der Variablen.
 *
 * - **Schrift auf dem Akzent** (`--auf-akzent`): dunkel oder weiß, was den
 *   höheren Kontrast hat. Erreicht keine von beiden 4,5 : 1, taugt die Farbe
 *   nicht als Handlungsfarbe — der Server nimmt sie dann nicht an.
 * - **Ring** um den mittigen Knopf: Unter 3 : 1 gegen die Anthrazit-Leiste
 *   (hell oder dunkel) verschwände der Kreis darin; er bekommt dann einen
 *   hellen Ring (WCAG 1.4.11).
 * - **Nah am Fehlerrot**: Ein Hauptknopf in der Farbe von „gesperrt" und
 *   „überfällig" sagt das Falsche. Nur ein Hinweis, kein Verbot.
 */
import { farbabstand, istHexfarbe, kontrast } from "./farben.js";
/** Die Vorgabe — derselbe Wert wie `--akzent` in tokens.css. */
export const AKZENT_VORGABE = '#F7A800';
/** Schrift auf dem Akzent: Tinte oder Weiß. */
export const AUF_AKZENT_DUNKEL = '#15181A';
export const AUF_AKZENT_HELL = '#FFFFFF';
/** Die Leiste (`--nav`) in Hell und Dunkel — gegen sie steht der mittige Knopf. */
export const LEISTE_HELL = '#2E3438';
export const LEISTE_DUNKEL = '#20262A';
/** Das Fehlerrot (`--stopp`, hell). */
export const FEHLERROT = '#B02A20';
/** Ab welchem OKLab-Abstand eine Farbe nicht mehr „wie Fehlerrot" aussieht. */
export const FEHLERROT_ABSTAND_MINDESTENS = 0.1;
/**
 * Die Vorschläge der Maske. `wort` ist ein i18n-Schlüssel — sichtbarer Text
 * steht nicht im Code. Die Werte sind RAL-Farben, gerundet auf das, was ein
 * Bildschirm von ihnen zeigt.
 */
export const AKZENT_VORSCHLAEGE = [
    { id: 'signalgelb', hex: '#F7A800', wort: 'erscheinungsbild.farbe.signalgelb' },
    { id: 'verkehrsorange', hex: '#DE5308', wort: 'erscheinungsbild.farbe.verkehrsorange' },
    { id: 'signalrot', hex: '#A52019', wort: 'erscheinungsbild.farbe.signalrot' },
    { id: 'enzianblau', hex: '#0E518D', wort: 'erscheinungsbild.farbe.enzianblau' },
    { id: 'himmelblau', hex: '#2271B3', wort: 'erscheinungsbild.farbe.himmelblau' },
    { id: 'smaragd', hex: '#287233', wort: 'erscheinungsbild.farbe.smaragd' },
    { id: 'wasserblau', hex: '#256D7B', wort: 'erscheinungsbild.farbe.wasserblau' },
    { id: 'anthrazit', hex: '#383E42', wort: 'erscheinungsbild.farbe.anthrazit' },
];
/** `#rrggbb` → `#RRGGBB`; alles andere ist `null`. */
export function akzentNormal(wert) {
    if (typeof wert !== 'string')
        return null;
    const w = wert.trim();
    return istHexfarbe(w) ? w.toUpperCase() : null;
}
/** Die Rollen zu einer Akzentfarbe. Wirft bei einem Wert, der keine Hexfarbe ist. */
export function akzentRollen(hex) {
    const akzent = akzentNormal(hex);
    if (!akzent)
        throw new Error(`akzent.hex: ${JSON.stringify(hex)}`);
    const dunkel = kontrast(akzent, AUF_AKZENT_DUNKEL);
    const hell = kontrast(akzent, AUF_AKZENT_HELL);
    const auf = dunkel >= hell ? AUF_AKZENT_DUNKEL : AUF_AKZENT_HELL;
    const kontrastAuf = Math.max(dunkel, hell);
    const kontrastLeiste = Math.min(kontrast(akzent, LEISTE_HELL), kontrast(akzent, LEISTE_DUNKEL));
    return {
        akzent,
        auf,
        kontrastAuf,
        kontrastLeiste,
        kontrastPapier: kontrast(akzent, '#FFFFFF'),
        ring: kontrastLeiste < 3,
        nahAmFehlerrot: farbabstand(akzent, FEHLERROT) < FEHLERROT_ABSTAND_MINDESTENS,
        taugt: kontrastAuf >= 4.5,
    };
}
//# sourceMappingURL=akzent.js.map